import { Button, DrawerCard, DrawerField, DrawerTitle, EmptyState, Input, Pill } from "@mixitone/components";
import type { Funder, FundingOutreach, FundingOutreachCommunication } from "@mixitone/models";
import EmailIcon from "@mixitone/icons/email.svg";
import DocIcon from "@mixitone/icons/doc.svg";
import { ApplicationView } from "@mixitone/mvc";
import { centsToDollars, dollarsToCents, formatOccurredAt } from "@mixitone/util";
import { SectionTabs } from "components/common/SectionTabs";
import { DrawerContextLink } from "components/common/DrawerContextLink";
import { RoutedDrawer } from "components/common/RoutedDrawer";
import Form from "components/Forms/Form";
import FormButtons from "components/Forms/FormButtons";
import { DrawerFormFieldset } from "components/Forms/FormRenderers/DrawerFormRenderer";
import { FunderFormController } from "components/Pages/Funding/FunderFormController";
import {
  FundingOutreachFormController,
  SIMULATED_REPLY_TEMPLATES,
} from "components/Pages/Funding/FundingOutreachFormController";
import FundingOutreachAcceptAmountModal from "components/Pages/Funding/FundingOutreachAcceptAmountModal";
import FundingOutreachAddMessageModal from "components/Pages/Funding/FundingOutreachAddMessageModal";
import FundingOutreachComposeModal from "components/Pages/Funding/FundingOutreachComposeModal";
import FundingOutreachViewEmailModal from "components/Pages/Funding/FundingOutreachViewEmailModal";
import { MessageStack } from "components/Pages/Funding/MessageStack";
import { isDevOrTestMode } from "lib/environment";
import { funderTypeLabel, FUNDING_OUTREACH_STATUS_SELECT_OPTIONS } from "lib/fundingStatusLabels";
import { sentimentLabel, sentimentStyles } from "lib/fundingSentiment";
import { injectModal } from "lib/injectParams";
import { useCallback, useMemo, useState } from "react";

const isDevOrTest = isDevOrTestMode();

const OUTREACH_DRAWER_TABS = [
  { id: "details", label: "Details" },
  { id: "messages", label: "Messages" },
] as const;

const FunderDrawerInlineForm = FunderFormController.scope(
  ApplicationView(
    (props: {
      clubId: string;
      id: string;
      onSubmit?: (record: Funder) => Promise<void>;
      onCancelEdit: () => void;
    }) => {
      const { onCancelEdit } = props;
      const funderController = FunderFormController.use();
      const fields = useMemo(() => Object.values(funderController.fields()), [funderController]);

      if (funderController.state.loading) {
        return (
          <DrawerCard title="Edit funder">
            <div className="animate-pulse text-sm text-gray-500">Loading…</div>
          </DrawerCard>
        );
      }

      return (
        <DrawerCard title="Edit funder">
          <Form name="funder">
            <DrawerFormFieldset fields={fields} />
            <div className="mt-4 flex justify-end gap-2">
              <Button type="button" kind="plain" onClick={onCancelEdit}>
                Cancel
              </Button>
              <FormButtons showDelete={false} showCancel={false} saveTitle="Save" />
            </div>
          </Form>
        </DrawerCard>
      );
    },
  ),
);

const EditApplicationDrawer = () => {
  const controller = FundingOutreachFormController.use();
  const initialTab = controller.props.tab === "messages" ? "messages" : "details";
  const [activeTabId, setActiveTabId] = useState<(typeof OUTREACH_DRAWER_TABS)[number]["id"]>(initialTab);
  const {
    loading,
    record,
    communications,
    latestReplyAnalysis,
    pendingReviewMessage,
    simulateTemplateIndex,
    simulatingReply,
    editingFunder,
  } = controller.state;
  const r = record as FundingOutreach | null;
  const funder = r?.funder;
  const contactEmail = funder?.contact_email?.trim();
  const appealEmailUnsubscribed = Boolean(r?.funding_email_opted_out_at);
  const clubFundingEmailUnsubscribed = Boolean(funder?.funding_email_opted_out_club_wide_at);
  const canSend = Boolean(contactEmail) && !appealEmailUnsubscribed && !clubFundingEmailUnsubscribed;

  const outbounds = communications.filter((c: FundingOutreachCommunication) => c.direction === "outbound");
  const inbounds = communications.filter((c: FundingOutreachCommunication) => c.direction === "inbound");
  const contactedAt =
    outbounds.length > 0
      ? outbounds.reduce(
          (best: FundingOutreachCommunication | null, c: FundingOutreachCommunication) =>
            !best || String(c.occurred_at) < String(best.occurred_at) ? c : best,
          null,
        )?.occurred_at
      : null;
  const lastActivity =
    communications.length > 0
      ? communications.reduce(
          (best: FundingOutreachCommunication | null, c: FundingOutreachCommunication) =>
            !best || String(c.occurred_at) > String(best.occurred_at) ? c : best,
          null,
        )?.occurred_at
      : null;
  const replyCount = inbounds.length;
  const latestSentimentLabel = latestReplyAnalysis ? sentimentLabel(latestReplyAnalysis.sentiment) : null;
  const summaryText = latestReplyAnalysis?.summary?.trim() || null;

  const status = r?.status ?? "to_contact";
  const potentialPounds = centsToDollars(r?.amount_potential_cents);

  const handleOpenCompose = useCallback(() => {
    if (!r?.id) return;
    controller.actionOpenCompose();
  }, [controller, r?.id]);

  const handleOpenReply = useCallback(
    (replyToSubject?: string) => {
      controller.actionOpenReply(replyToSubject);
    },
    [controller],
  );

  const handleSimulateReply = useCallback(() => {
    controller.actionSimulateReply(simulateTemplateIndex);
  }, [controller, simulateTemplateIndex]);

  const handleFunderSaved = useCallback(async () => {
    await controller.actionReloadFunder();
    controller.actionEditFunder(false);
  }, [controller]);

  const handleFunderCancelEdit = useCallback(() => {
    void (async () => {
      await controller.actionReloadFunder();
      controller.actionEditFunder(false);
    })();
  }, [controller]);

  const openMessagesTab = useCallback(() => {
    setActiveTabId("messages");
  }, []);

  if (loading) return <RoutedDrawer spinner={loading} />;

  return (
    <RoutedDrawer>
      <div className="flex flex-col h-full">
        <DrawerTitle
          icon={DocIcon}
          contextLink={<DrawerContextLink context="funding" params={{ clubId: controller.props.clubId }} />}
        >
          {funder?.name?.trim() || "Application"}
        </DrawerTitle>

        <div className="flex min-h-0 flex-1 flex-col gap-6">
          <div className="-mx-4">
            <SectionTabs
              tabs={[...OUTREACH_DRAWER_TABS]}
              activeTabId={activeTabId}
              onChange={(tabId) => setActiveTabId(tabId === "messages" ? "messages" : "details")}
            />
          </div>

          {activeTabId === "details" && (
            <>
              {editingFunder && funder?.id ? (
                <FunderDrawerInlineForm
                  key={funder.id}
                  clubId={controller.props.clubId}
                  id={funder.id}
                  onSubmit={handleFunderSaved}
                  onCancelEdit={handleFunderCancelEdit}
                />
              ) : (
                <DrawerCard title="Funder">
                  <div className="flex flex-col gap-3 text-sm">
                    <DrawerField label="Name">{funder?.name?.trim() || "—"}</DrawerField>
                    <DrawerField label="Type">{funderTypeLabel(funder?.funder_type)}</DrawerField>
                    <DrawerField label="Contact">{funder?.contact_name?.trim() || "—"}</DrawerField>
                    <DrawerField label="Email">{funder?.contact_email?.trim() || "—"}</DrawerField>
                    {clubFundingEmailUnsubscribed ? (
                      <DrawerField label="Email subscription">
                        <Pill color="outlineRed">Unsubscribed from all club funding emails</Pill>
                      </DrawerField>
                    ) : null}
                    <DrawerField label="Phone">{funder?.contact_phone?.trim() || "—"}</DrawerField>
                    <DrawerField label="Website">
                      {funder?.website?.trim() ? (
                        <a
                          href={funder.website.trim()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="break-all text-brand-700 underline-offset-2 hover:underline"
                        >
                          {funder.website.trim()}
                        </a>
                      ) : (
                        "—"
                      )}
                    </DrawerField>
                    {funder?.note?.trim() ? (
                      <DrawerField label="Note">
                        <span className="whitespace-pre-wrap">{funder.note.trim()}</span>
                      </DrawerField>
                    ) : null}
                    <div className="flex justify-end pt-1">
                      {funder?.id ? (
                        <Button kind="action" onClick={() => controller.actionEditFunder(true)}>
                          Edit funder
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </DrawerCard>
              )}

              <DrawerCard title="Outreach">
                <div className="flex flex-col gap-3 text-sm">
                  <DrawerField label="Created by">
                    <span className="text-gray-900">
                      {(r as FundingOutreach & { created_by_account_user?: { name?: string | null } })
                        ?.created_by_account_user?.name ?? "—"}
                    </span>
                  </DrawerField>
                  <DrawerField label="Assigned to" htmlFor="funding-outreach-assigned-to">
                    <select
                      id="funding-outreach-assigned-to"
                      value={r?.assigned_to_account_user_id ?? ""}
                      onChange={(e) => controller.actionSetAssignedTo(e.target.value || null)}
                      className="rounded border border-gray-300 bg-white px-2 py-1.5 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">—</option>
                      {controller.state.accountUsers.map((au) => (
                        <option key={au.id} value={au.id}>
                          {au.name ?? "—"}
                        </option>
                      ))}
                    </select>
                  </DrawerField>
                  <DrawerField label="Status" htmlFor="funding-outreach-status">
                    <div className="flex w-full flex-wrap items-center gap-2">
                      <select
                        id="funding-outreach-status"
                        value={status}
                        onChange={(e) => {
                          const newStatus = e.target.value;
                          if (newStatus === "approved") {
                            controller.actionOpenAcceptAmountModal();
                          } else {
                            void controller.actionSetStatus(newStatus as FundingOutreach["status"]);
                          }
                        }}
                        className="rounded border border-gray-300 bg-white px-2 py-1.5 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        {FUNDING_OUTREACH_STATUS_SELECT_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      {contactedAt != null && (
                        <span className="text-sm text-gray-500">
                          Contacted {formatOccurredAt(contactedAt)}
                        </span>
                      )}
                    </div>
                  </DrawerField>
                  {appealEmailUnsubscribed ? (
                    <DrawerField label="Email subscription">
                      <Pill color="outlineRed">Unsubscribed from this funding application</Pill>
                    </DrawerField>
                  ) : null}
                  <DrawerField
                    label="Indicated"
                    helpText="Amount the funder has indicated (the assistant can fill from replies if blank)."
                  >
                    <Input
                      type="number"
                      min={0}
                      step={0.01}
                      className="max-w-[7rem] rounded border-gray-300 px-2 py-1.5 text-sm"
                      placeholder="—"
                      value={potentialPounds != null ? String(potentialPounds) : ""}
                      onChange={(e) => {
                        const v = e.target.value.trim();
                        const r2 = controller.state.record as FundingOutreach | null;
                        const cents = dollarsToCents(v);
                        const valid = v === "" || cents !== undefined;
                        if (r2 && valid) {
                          r2.amount_potential_cents = cents;
                        }
                      }}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        const n = v === "" ? null : Number.parseFloat(v);
                        if (v === "" || (n != null && Number.isFinite(n) && n >= 0)) {
                          controller.actionUpdatePotentialAmount(v === "" ? null : n);
                        }
                      }}
                      title="Indicated amount (the assistant can fill from replies if blank)"
                    />
                  </DrawerField>
                  {(status === "contacted" && replyCount > 0) || status === "replied" ? (
                    <DrawerField label="Next steps">
                      <div className="flex flex-wrap gap-1.5">
                        {status === "contacted" && replyCount > 0 && (
                          <Button
                            kind="secondary"
                            className="!py-1 !text-xs"
                            onClick={() => controller.actionSetStatusReplied()}
                            title="Mark that the funder has replied"
                          >
                            Mark replied
                          </Button>
                        )}
                        {status === "replied" && (
                          <>
                            <Button
                              kind="primary"
                              className="!py-1 !text-xs"
                              onClick={() => controller.actionOpenAcceptAmountModal()}
                              title="Record that funding was accepted"
                            >
                              Funding
                            </Button>
                            <Button
                              kind="secondary"
                              className="!py-1 !text-xs"
                              onClick={() => controller.actionSetStatusDeclined()}
                              title="Record that the funder declined"
                            >
                              Declining
                            </Button>
                          </>
                        )}
                      </div>
                    </DrawerField>
                  ) : null}
                </div>
              </DrawerCard>

              {(replyCount > 0 || Boolean(summaryText)) && (
                <section
                  role="button"
                  tabIndex={0}
                  onClick={openMessagesTab}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openMessagesTab();
                    }
                  }}
                  aria-label="View messages"
                  className={`cursor-pointer rounded-lg border border-l-4 bg-white p-4 shadow-sm transition-colors hover:bg-gray-50 ${
                    replyCount > 0 && latestSentimentLabel
                      ? sentimentStyles(latestReplyAnalysis?.sentiment ?? "unknown").border
                      : "border-l-gray-300"
                  }`}
                >
                  <div className="space-y-3">
                    {replyCount > 0 && (
                      <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                        <strong>
                          {replyCount} repl{replyCount === 1 ? "y" : "ies"}
                        </strong>
                        {latestSentimentLabel ? (
                          <span
                            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
                              sentimentStyles(latestReplyAnalysis?.sentiment ?? "unknown").badge
                            }`}
                          >
                            {latestSentimentLabel}
                          </span>
                        ) : (
                          <span className="text-gray-500">(analyzing…)</span>
                        )}
                        {lastActivity && (
                          <>
                            <span>· Last activity</span>
                            <strong>{formatOccurredAt(lastActivity)}</strong>
                          </>
                        )}
                      </div>
                    )}
                    {summaryText && (
                      <blockquote
                        className={`rounded border-l-2 py-2 pl-3 pr-2 text-sm ${
                          sentimentStyles(latestReplyAnalysis?.sentiment ?? "unknown").summaryBg
                        }`}
                      >
                        &ldquo;{summaryText}&rdquo;
                      </blockquote>
                    )}
                  </div>
                </section>
              )}

              {pendingReviewMessage && (
                <section className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  Thanks — your email is being processed. It will appear in this thread once it has been sent.
                </section>
              )}
            </>
          )}

          {activeTabId === "messages" && (
            <section className="flex min-h-0 flex-1 flex-col">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <EmailIcon className="h-4 w-4" />
                  Messages
                </h3>
                <div className="flex items-center gap-2">
                  {isDevOrTest && (
                    <div className="flex items-center gap-2">
                      <select
                        value={simulateTemplateIndex}
                        onChange={(e) => controller.actionSetSimulateTemplateIndex(Number(e.target.value))}
                        className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        title="Choose a realistic reply to simulate"
                      >
                        {SIMULATED_REPLY_TEMPLATES.map((t, i) => (
                          <option key={t.id} value={i}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                      <Button
                        kind="secondary"
                        onClick={handleSimulateReply}
                        disabled={simulatingReply || !r?.id}
                        spinner={simulatingReply}
                        title="Simulate an inbound reply (local dev only)"
                      >
                        Simulate reply (dev)
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              {!canSend && (
                <p className="mb-2 rounded bg-amber-50 p-2 text-sm text-amber-800">
                  {!contactEmail
                    ? "Add a contact email for this funder to send emails."
                    : clubFundingEmailUnsubscribed
                      ? "This funder has unsubscribed from all funding emails from your club. Messages are still shown below, but new emails cannot be sent."
                      : appealEmailUnsubscribed
                        ? "This funder has unsubscribed from emails for this application. Messages are still shown below, but new emails cannot be sent."
                        : null}
                </p>
              )}
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden pr-1">
                {communications.length === 0 ? (
                  <EmptyState>
                    <p className="text-sm text-gray-500">
                      No messages yet. Add a message (e.g. phone notes, pasted email) or send an email below.
                    </p>
                  </EmptyState>
                ) : (
                  <MessageStack />
                )}
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <Button
                  kind="secondary"
                  onClick={() => controller.actionOpenAddMessageModal()}
                  title="Log a phone call, SMS, or pasted email"
                >
                  Add message
                </Button>
                {canSend && (
                  <Button
                    kind="primary"
                    onClick={() =>
                      replyCount > 0
                        ? handleOpenReply(communications[communications.length - 1]?.subject)
                        : handleOpenCompose()
                    }
                    title={replyCount > 0 ? "Reply to the funder" : "Compose a new email"}
                  >
                    {replyCount > 0 ? "Reply" : "Compose"}
                  </Button>
                )}
              </div>
            </section>
          )}
        </div>
      </div>

      <FundingOutreachViewEmailModal />
      <FundingOutreachAcceptAmountModal />
      <FundingOutreachComposeModal />
      <FundingOutreachAddMessageModal />
    </RoutedDrawer>
  );
};

export default injectModal(FundingOutreachFormController.scope(ApplicationView(EditApplicationDrawer)));
